import { eventsApiBaseUrl } from "../config/eventsApi";

const FETCH_TIMEOUT_MS = 8000;
const IST = "Asia/Kolkata";

export class EventsApiError extends Error {
  constructor(message, { code = "", field = "", status = 0, retryAfter = "" } = {}) {
    super(message);
    this.name = "EventsApiError";
    this.code = code;
    this.field = field;
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

const DEFAULT_FORM_OPTIONS = {
  collectDob: false,
  collectGender: false,
  memberChoice: false,
  paymentRequired: false,
  teamSize: null,
  notesQuestion: null,
  notesRequired: false,
  minAgeYears: null,
  ageAsOn: null,
  paymentInstructions: null,
  whatsappGroupPublic: false,
  whatsappGroupUrl: null,
};

const CLOSED_STATUS_LABELS = {
  full: "Sold Out",
  deadline: "Registrations Closed",
  closed: "Registrations Closed",
  archived: "Registrations Closed",
};

export const NO_REGISTRATION_NEEDED_LABEL = "No registration needed";
export const REGISTRATIONS_OPENING_SOON_LABEL = "Registrations opening soon";
export const REGISTRATION_COMING_SOON_MESSAGE =
  "Registrations are opening soon. Please check back later.";

/**
 * Public group link on GET /api/public/events (smym-app PR #15).
 * The API returns `formOptions.whatsappGroupUrl` only when
 * `formOptions.whatsappGroupPublic` is true. `publicWhatsappGroupUrl` and a
 * top-level `whatsappGroupUrl` remain fallbacks.
 */
const PUBLIC_WHATSAPP_KEYS = ["publicWhatsappGroupUrl", "whatsappGroupUrl"];

/** Trimmed http(s) URL with a host, or "" for anything else. */
function safeHttpUrl(value, { httpsOnly = false } = {}) {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return "";
  let url;
  try {
    url = new URL(raw);
  } catch {
    return "";
  }
  const protocolOk =
    url.protocol === "https:" || (!httpsOnly && url.protocol === "http:");
  if (!protocolOk || !url.hostname || url.username || url.password) return "";
  return raw;
}

/** Trimmed https URL with a host, or "" for anything else. */
function safeHttpsUrl(value) {
  return safeHttpUrl(value, { httpsOnly: true });
}

/**
 * Optional `venueMapUrl` on GET /api/public/events (smym-app PR #44).
 * Missing, null, and non-http(s) values stay empty so the venue name is plain text.
 */
export function eventVenueMapUrl(event) {
  if (!event || typeof event !== "object") return "";
  return safeHttpUrl(event.venueMapUrl);
}

/** True when the event does not take registrations (missing fields stay false). */
export function eventRegistrationNotRequired(event) {
  if (!event || typeof event !== "object") return false;
  return (
    event.registrationNotRequired === true ||
    event.registrationClosedReason === "not_required"
  );
}

/** True when registration will open later. `not_required` stays the stronger signal. */
export function eventRegistrationComingSoon(event) {
  if (!event || typeof event !== "object") return false;
  if (eventRegistrationNotRequired(event)) return false;
  return event.registrationClosedReason === "coming_soon";
}

/** Sanitized public WhatsApp group URL, or "" when the API does not expose one. */
export function publicWhatsappGroupUrl(event) {
  if (!event || typeof event !== "object") return "";
  const formOptions = event.formOptions;
  if (
    formOptions &&
    typeof formOptions === "object" &&
    formOptions.whatsappGroupPublic === true
  ) {
    const fromForm = safeHttpsUrl(formOptions.whatsappGroupUrl);
    if (fromForm) return fromForm;
  }
  for (const key of PUBLIC_WHATSAPP_KEYS) {
    const url = safeHttpsUrl(event[key]);
    if (url) return url;
  }
  return "";
}

/**
 * https Google Maps links from smym-app PR #17.
 * Allowed hosts: google.com/maps, www.google.com/maps, maps.google.com,
 * maps.app.goo.gl, and goo.gl/maps. Anything else is dropped.
 */
export function publicGoogleMapsUrl(value) {
  const raw = safeHttpsUrl(value);
  if (!raw) return "";
  let url;
  try {
    url = new URL(raw);
  } catch {
    return "";
  }
  const host = url.hostname.toLowerCase().replace(/\.$/, "");
  const path = url.pathname;
  const mapsPath = path === "/maps" || path.startsWith("/maps/");
  const allowed =
    ((host === "google.com" || host === "www.google.com") && mapsPath) ||
    host === "maps.google.com" ||
    host === "maps.app.goo.gl" ||
    (host === "goo.gl" && mapsPath);
  return allowed ? raw : "";
}

/**
 * Drop points on GET /api/public/events (smym-app PR #17).
 * Missing, empty, and malformed lists stay hidden. `mapsUrl` is null unless
 * it is an allowed Google Maps link.
 */
export function publicDropPoints(event) {
  const raw = event?.dropPoints;
  if (!Array.isArray(raw)) return [];
  const points = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const address = typeof item.address === "string" ? item.address.trim() : "";
    if (!address) continue;
    const label = typeof item.label === "string" ? item.label.trim() : "";
    const mapsUrl = publicGoogleMapsUrl(item.mapsUrl);
    points.push({
      label: label || null,
      address,
      mapsUrl: mapsUrl || null,
    });
  }
  return points;
}

/** Pickup is offered only when the API sets `pickupEnabled` and the event has a slug. */
export function eventPickupEnabled(event) {
  return Boolean(
    event &&
    typeof event === "object" &&
    event.pickupEnabled === true &&
    typeof event.slug === "string" &&
    event.slug.trim() !== "",
  );
}

/** 10-digit Indian mobile. Accepts +91, 91, and a single leading 0. */
export function normalizeIndianMobile(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  let compact = trimmed.replace(/[\s().-]/g, "");
  if (compact.startsWith("+")) compact = compact.slice(1);
  if (!/^\d+$/.test(compact)) return "";
  if (compact.startsWith("91") && (compact.length === 12 || compact.length === 13)) {
    compact = compact.slice(2);
  }
  if (compact.startsWith("0") && compact.length === 11) compact = compact.slice(1);
  return /^[6-9]\d{9}$/.test(compact) ? compact : "";
}

export function pickupFieldError(field, value) {
  switch (field) {
    case "name": {
      const name = String(value ?? "").trim();
      if (!name) return "Name is required.";
      if (name.length < 2 || name.length > 100) {
        return "Enter a name between 2 and 100 characters.";
      }
      return "";
    }
    case "phone":
      return normalizeIndianMobile(value)
        ? ""
        : "Enter a 10-digit Indian mobile number.";
    case "address": {
      const address = String(value ?? "").trim();
      if (!address) return "Address is required.";
      if (address.length < 5 || address.length > 500) {
        return "Enter an address between 5 and 500 characters.";
      }
      return "";
    }
    default:
      return "";
  }
}

function retryAfterPhrase(retryAfter) {
  const raw = String(retryAfter ?? "").trim();
  if (!raw) return "";
  if (/^\d+$/.test(raw)) {
    const seconds = Number(raw);
    if (!Number.isFinite(seconds) || seconds <= 0) return "";
    if (seconds === 1) return "1 second";
    if (seconds < 60) return `${seconds} seconds`;
    const minutes = Math.ceil(seconds / 60);
    return minutes === 1 ? "1 minute" : `${minutes} minutes`;
  }
  const when = new Date(raw);
  if (Number.isNaN(when.getTime())) return "";
  const clock = when.toLocaleTimeString("en-IN", {
    timeZone: IST,
    hour: "numeric",
    minute: "2-digit",
  });
  return clock ? `after ${clock}` : "";
}

/** Friendly copy for POST /pickup-requests error codes. */
export function pickupRequestErrorMessage(code, { field = "", retryAfter = "" } = {}) {
  switch (code) {
    case "validation_failed":
      if (field === "name") return "Enter a name between 2 and 100 characters.";
      if (field === "phone") return "Enter a 10-digit Indian mobile number.";
      if (field === "address") return "Enter an address between 5 and 500 characters.";
      if (field === "cf-turnstile-response") {
        return "The security check failed. Please complete it and try again.";
      }
      return "Please check the form and try again.";
    case "invalid_request":
      return "That request could not be processed. Please try again.";
    case "turnstile_failed":
      return "The security check failed. Please complete it and try again.";
    case "event_not_found":
      return "This event could not be found.";
    case "pickup_closed":
      return "Pickup requests are closed for this event.";
    case "rate_limited": {
      const wait = retryAfterPhrase(retryAfter);
      if (!wait) return "Too many requests. Please wait a moment and try again.";
      if (wait.startsWith("after ")) {
        return `Too many requests. Please try again ${wait}.`;
      }
      return `Too many requests. Please try again in ${wait}.`;
    }
    default:
      return "Could not send the pickup request. Please try again.";
  }
}

/**
 * JSON body for POST /api/public/events/:slug/pickup-requests.
 * `company` is the honeypot and stays empty for a real visitor.
 * Pass `includeTurnstile` when the registration form would send a token.
 */
export function buildPickupRequestPayload(
  { name, phone, address, company = "", turnstileToken = "" },
  { includeTurnstile = false } = {},
) {
  const payload = {
    name: String(name ?? "").trim(),
    phone: normalizeIndianMobile(phone),
    address: String(address ?? "").trim(),
    company: String(company ?? ""),
  };
  if (includeTurnstile) {
    payload["cf-turnstile-response"] = String(turnstileToken ?? "");
  }
  return payload;
}

/** Register is offered only for an open event that still requires registration. */
export function canRegisterForEvent(event) {
  return Boolean(
    event &&
    event.slug &&
    event.registrationOpen === true &&
    !eventRegistrationNotRequired(event) &&
    !eventRegistrationComingSoon(event),
  );
}

/** True when the event is stored at 11:59 PM IST, i.e. it runs until the end of that day. */
export function isEndOfDayIst(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const hm = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
  return hm === "23:59";
}

/** `HH:MM` in IST, or "" when the event is stored at IST midnight (date only). */
function istTimeLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-IN", {
    timeZone: IST,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(d);
  const hour = parts.find((p) => p.type === "hour")?.value;
  const minute = parts.find((p) => p.type === "minute")?.value;
  const period = parts.find((p) => p.type === "dayPeriod")?.value?.toUpperCase();
  if (hour === "12" && minute === "00" && period === "AM") return "";
  // 11:59 PM is the admin's "end of day" sentinel, not a real start time.
  if (hour === "11" && minute === "59" && period === "PM") return "";
  return `${hour}:${minute} ${period}`;
}

function splitSponsorMention(specialMention) {
  const s = String(specialMention || "").trim();
  const m = /^sponsor(?:s|ed by)?\s*:\s*(.+)$/i.exec(s);
  if (m) return { sponsors: [m[1].trim()], specialMention: "" };
  return { sponsors: [], specialMention: s };
}

/**
 * Public `image` is null, an API path `/api/public/events/<slug>/flyer`,
 * or a static site path / absolute URL for older seeded events.
 * API paths load from REACT_APP_API_BASE_URL. Other values stay on this site
 * (or as an absolute URL).
 * @param {unknown} image
 * @param {string} [apiBase]
 */
export function eventFlyerSrc(image, apiBase = eventsApiBaseUrl()) {
  const value = typeof image === "string" ? image.trim() : "";
  if (!value) return "";
  // Keep ?v= and any other query. These URLs are opaque.
  if (/^(https?:)?\/\//i.test(value)) return value;
  if (value.startsWith("/api/")) {
    const base = String(apiBase || "")
      .trim()
      .replace(/\/+$/, "");
    return base ? `${base}${value}` : value;
  }
  return value;
}

/**
 * Card photo vs popup flyer.
 * `cardImage` wins for the card; otherwise the flyer is shown whole.
 * Static fallback events only have `image` or `flyer`.
 */
export function eventCardMedia(event) {
  if (!event || typeof event !== "object") {
    return { flyer: "", cardImage: "", src: "", fit: "contain" };
  }
  const rawFlyer =
    typeof event.flyer === "string" && event.flyer.trim() ? event.flyer : event.image;
  const flyer = eventFlyerSrc(rawFlyer);
  const cardImage = eventFlyerSrc(event.cardImage);
  return {
    flyer,
    cardImage,
    src: cardImage || flyer,
    fit: cardImage ? "cover" : "contain",
  };
}

function feeAmount(value) {
  if (value == null || value === "") return null;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return amount;
}

function positiveCount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return amount;
}

/** Site path (`/bulandi-2026`) or https URL. Drops protocol-relative and other schemes. */
export function eventDetailsHref(value) {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw || raw.startsWith("//") || raw.startsWith("/\\")) return "";
  if (raw.startsWith("/")) return raw;
  return safeHttpsUrl(raw);
}

/** `name|YYYY-MM-DD`, using the date prefix so ISO timestamps match a calendar day. */
export function pastEventKey(event) {
  const name = String(event?.name || event?.title || "")
    .trim()
    .toLowerCase();
  const raw = typeof event?.date === "string" ? event.date.trim() : "";
  const date = raw.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] || "";
  return `${name}|${date}`;
}

/**
 * Published past events from the API when that list has anything.
 * Bundled `eventsData` past events are the offline / empty-feed fallback.
 * Site-local archive cards (Bulandi 2026 is members-only, so it is absent
 * from the public feed) are always merged into an API list. The same name
 * and calendar date collapse to one card; the API copy wins, and the
 * site-local details link is kept when that copy has none.
 */
export function selectPastEvents(apiPast, fallbackPast = [], pinnedPast = []) {
  const api = Array.isArray(apiPast) ? apiPast.filter(Boolean) : [];
  const fallback = Array.isArray(fallbackPast) ? fallbackPast : [];
  const pinned = Array.isArray(pinnedPast) ? pinnedPast.filter(Boolean) : [];
  const source = api.length > 0 ? [...api, ...pinned] : fallback;
  const seen = new Map();
  const unique = [];
  for (const event of source) {
    const key = pastEventKey(event);
    if (seen.has(key)) {
      const index = seen.get(key);
      const kept = unique[index];
      if (!kept.detailsPath && event.detailsPath) {
        unique[index] = {
          ...kept,
          detailsPath: event.detailsPath,
          detailsLabel: kept.detailsLabel || event.detailsLabel || "",
        };
      }
      continue;
    }
    seen.set(key, unique.length);
    unique.push(event);
  }
  return unique.sort((a, b) => (Date.parse(b?.date) || 0) - (Date.parse(a?.date) || 0));
}

/**
 * "Members ₹X · Others ₹Y" or a single "₹X" when a fee is set.
 * Null when both fees are missing — a missing fee is not free.
 * Blank when the event does not take registration.
 */
export function eventFeeLabel(event) {
  if (!event || typeof event !== "object" || eventRegistrationNotRequired(event)) {
    return "";
  }
  const member = feeAmount(event.feeMember);
  const other = feeAmount(event.feeNonMember);
  if (member == null && other == null) return null;
  if (member != null && other != null && member !== other) {
    return `Members ${formatRupees(member)} · Others ${formatRupees(other)}`;
  }
  return formatRupees(member != null ? member : other);
}

/** Maps an API event onto the card shape EventsSection already renders. */
export function toEventCard(apiEvent) {
  const formOptions = { ...DEFAULT_FORM_OPTIONS, ...(apiEvent.formOptions || {}) };
  const { sponsors, specialMention } = splitSponsorMention(apiEvent.specialMention);
  const registrationNotRequired = eventRegistrationNotRequired(apiEvent);
  const registrationComingSoon = eventRegistrationComingSoon(apiEvent);
  const registrationOpen =
    apiEvent.registrationOpen === true &&
    !registrationNotRequired &&
    !registrationComingSoon;
  return {
    ...apiEvent,
    id: apiEvent.id,
    slug: apiEvent.slug,
    name: apiEvent.title,
    date: apiEvent.date,
    time: istTimeLabel(apiEvent.date),
    endsAtEndOfDay: isEndOfDayIst(apiEvent.date),
    venue: apiEvent.location || "",
    venueMapUrl: eventVenueMapUrl(apiEvent),
    description: apiEvent.description || "",
    flyer: eventFlyerSrc(apiEvent.image),
    cardImage: eventFlyerSrc(apiEvent.cardImage),
    sponsors,
    specialMention,
    coordinator: apiEvent.coordinator || "",
    attendees:
      positiveCount(apiEvent.attendees) ?? positiveCount(apiEvent.participantCount),
    detailsPath: eventDetailsHref(apiEvent.detailsUrl),
    detailsLabel:
      typeof apiEvent.detailsLabel === "string" ? apiEvent.detailsLabel.trim() : "",
    galleryAlbumSlug:
      typeof apiEvent.galleryAlbumSlug === "string"
        ? apiEvent.galleryAlbumSlug.trim()
        : "",
    status: registrationNotRequired
      ? NO_REGISTRATION_NEEDED_LABEL
      : registrationComingSoon
        ? REGISTRATIONS_OPENING_SOON_LABEL
        : registrationOpen
          ? "Registrations Open"
          : CLOSED_STATUS_LABELS[apiEvent.registrationClosedReason] ||
            "Registrations Closed",
    registrationOpen,
    registrationNotRequired,
    publicWhatsappGroupUrl: publicWhatsappGroupUrl(apiEvent),
    dropPoints: publicDropPoints(apiEvent),
    pickupEnabled: apiEvent.pickupEnabled === true,
    formOptions,
  };
}

function apiUrl(path) {
  return `${eventsApiBaseUrl()}${path}`;
}

async function readJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Published (upcoming) and archived (past) public events.
 * @returns {Promise<{ upcoming: object[]; past: object[] }>}
 */
export async function fetchPublicEvents({ signal } = {}) {
  if (!eventsApiBaseUrl())
    throw new EventsApiError("REACT_APP_API_BASE_URL is not set.");
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  const onAbort = () => ctrl.abort();
  signal?.addEventListener("abort", onAbort);
  try {
    const res = await fetch(apiUrl("/api/public/events?state=all"), {
      signal: ctrl.signal,
      headers: { Accept: "application/json" },
    });
    const data = await readJson(res);
    if (!res.ok || !data?.ok || !Array.isArray(data.events)) {
      throw new EventsApiError("Could not load events.", { status: res.status });
    }
    const cards = data.events.map(toEventCard);
    return {
      upcoming: cards.filter((e) => e.publishState === "published"),
      past: cards.filter((e) => e.publishState === "archived"),
    };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}

/**
 * @param {string} slug
 * @param {FormData} formData multipart body incl. `cf-turnstile-response`
 */
export async function submitPublicEventRegistration(slug, formData) {
  let res;
  try {
    res = await fetch(
      apiUrl(`/api/public/events/${encodeURIComponent(slug)}/registrations`),
      {
        method: "POST",
        body: formData,
      },
    );
  } catch {
    throw new EventsApiError("Network error. Check your connection and try again.", {
      code: "network_error",
    });
  }
  const data = await readJson(res);
  if (!res.ok || !data?.ok) {
    const code = data?.error || "";
    throw new EventsApiError(
      (code === "registration_coming_soon" ? REGISTRATION_COMING_SOON_MESSAGE : "") ||
        data?.message ||
        "Registration failed. Please try again.",
      {
        code,
        field: data?.field || "",
        status: res.status,
      },
    );
  }
  return data;
}

/**
 * @param {string} slug
 * @param {object} body JSON incl. name, phone, address, company, and
 *   `cf-turnstile-response` when the registration form would send one
 */
export async function submitPublicPickupRequest(slug, body) {
  let res;
  try {
    res = await fetch(
      apiUrl(`/api/public/events/${encodeURIComponent(slug)}/pickup-requests`),
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      },
    );
  } catch {
    throw new EventsApiError("Network error. Check your connection and try again.", {
      code: "network_error",
    });
  }
  const data = await readJson(res);
  if (res.status === 201 && data?.ok === true && data.pickupRequest) return data;
  const retryAfter = res.headers?.get?.("Retry-After") || "";
  const code = data?.error || (res.status === 429 ? "rate_limited" : "");
  const field = data?.field || "";
  throw new EventsApiError(pickupRequestErrorMessage(code, { field, retryAfter }), {
    code,
    field,
    status: res.status,
    retryAfter,
  });
}

export function formatRupees(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "";
  return `\u20B9${Number(amount).toLocaleString("en-IN")}`;
}
